import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from '../utils/toast';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { MediaForm, type MediaFormValues } from '../components/media/MediaForm';
import { useCreateMedia } from '../api/media';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { FiArrowLeft } from 'react-icons/fi';

import { ProfileGuard } from '../components/auth/ProfileGuard';

export const Route = createFileRoute('/media/create')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: CreateMedia,
});

function CreateMedia() {
  const navigate = useNavigate();
  const createMedia = useCreateMedia();
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async (values: MediaFormValues) => {
    if (!values.mediaType) return;

    try {
      setIsUploading(true);

      // 1. Upload new local files to Cloudinary (if any)
      let secureUrls: string[] = [...values.existingUrls];

      if (values.files.length > 0) {
        try {
          const results = await uploadFilesToCloudinary(values.files, {
            folder: 'event_images',
            uploadPreset: 'event_preset',
          });
          secureUrls = [...secureUrls, ...results.map((r) => r.secure_url)];
        } catch (uploadErr) {
          console.error('Cloudinary upload error:', uploadErr);
          toast.error('Upload failed. Please try again.');
          return;
        }
      }

      if (secureUrls.length === 0) {
        toast.error('Please upload at least one media file.');
        return;
      }

      // 2. Send URLs to the backend
      await createMedia.mutateAsync({
        title: values.title || undefined,
        media_urls: secureUrls,
        media_type: values.mediaType,
        caption: values.caption || undefined,
        service_id: values.service?.id ?? undefined,
        event_id: values.event?.id ?? undefined,
        collaborator_ids: values.collaboratorIds.length ? values.collaboratorIds : undefined,
        website: values.platformLinks.website || undefined,
        spotify: values.platformLinks.spotify || undefined,
        soundcloud: values.platformLinks.soundcloud || undefined,
        apple_music: values.platformLinks.appleMusic || undefined,
        amazon_music: values.platformLinks.amazonMusic || undefined,
        youtube_music: values.platformLinks.youtubeMusic || undefined,
      });

      navigate({ to: '/media' });
    } catch (err) {
      console.error('Create media error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <ProfileGuard>
        <main className="pt-24 md:pt-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-white/5 rounded-full transition-colors group"
            >
              <FiArrowLeft
                size={24}
                className="text-zinc-400 group-hover:text-white transition-colors"
              />
            </button>
          </div>

          <MediaForm
            title="New Media"
            submitLabel="Finish"
            onSubmit={handleSubmit}
            isLoading={isUploading || createMedia.isPending}
          />
        </main>
      </ProfileGuard>

      <BottomNav />
    </div>
  );
}
